import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import {
  Table,
  Text,
  theme,
  RangePicker,
  message,
} from '@brokerbay/design-components';
import { Card } from '@bb/elements-web';
import type { KeyholderInvoiceData } from '@/app-package';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import { formatCurrency } from 'utils';
import { Button } from 'components';
import { getKeyholderInvoices } from 'api/SupraController';
import sendSupraBillingHistoryEmail from 'api/SupraController/sendSupraBillingHistoryEmail';
import { FilterValue } from 'antd/lib/table/interface';
import { useSubscriptionsPageContext } from '../../SubscriptionsPageContext';
import { Invoice, InvoiceStatus, InvoiceFilters } from './types';
import StatusFilterDropdown from './components/StatusFilterDropdown';

// ============================================================================
// Constants
// ============================================================================

const COLUMN_WIDTH = 204;
const AMOUNT_MIN_WIDTH = 60;
const DATE_PICKER_WIDTH = 320;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Maps raw API response to our Invoice type.
 * Transforms date strings to Date objects and normalizes status values.
 */
function mapInvoiceResponse(response: KeyholderInvoiceData): Invoice {
  return {
    id: String(response.billingPlatformInvoiceId),
    invoiceNumber: response.invoiceNumber,
    createdAt: new Date(response.createdAt),
    amount: response.amount,
    status:
      response.status === 'PAID' ? InvoiceStatus.Paid : InvoiceStatus.Unpaid,
  };
}

/**
 * Filters invoices based on status and date range.
 * All filters are optional and applied cumulatively.
 */
function filterInvoices(
  invoices: Invoice[],
  filters: InvoiceFilters,
): Invoice[] {
  let filtered = invoices;

  if (filters.status) {
    filtered = filtered.filter(invoice => invoice.status === filters.status);
  }

  if (filters.createdAfter) {
    filtered = filtered.filter(
      invoice => new Date(invoice.createdAt) >= filters.createdAfter!,
    );
  }

  if (filters.createdBefore) {
    filtered = filtered.filter(
      invoice => new Date(invoice.createdAt) <= filters.createdBefore!,
    );
  }

  return filtered;
}

// ============================================================================
// Component
// ============================================================================

/**
 * Displays a list of keyholder billing invoices with filtering and email capabilities.
 *
 * Features:
 * - Fetches invoices from the billing API
 * - Filter by payment status (Paid/Unpaid)
 * - Filter by date range
 * - Email invoice history to user
 *
 * Requires SubscriptionsPageProvider to be present in the component tree.
 */
const InvoicesCard: React.FC = () => {
  const { billingProfile, loadingBillingProfile } =
    useSubscriptionsPageContext();

  // Check if billing profile exists
  const hasBillingProfile =
    !!billingProfile?.BoardBillingNumber && !!billingProfile?.UserBillingNumber;

  // Data state
  const [allInvoices, setAllInvoices] = useState<Invoice[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [sendingBillingHistoryEmail, setSendingBillingHistoryEmail] =
    useState(false);

  // Filter state
  const [filters, setFilters] = useState<InvoiceFilters>({
    status: undefined,
    createdAfter: undefined,
    createdBefore: undefined,
  });

  // Memoize filtered data source to avoid recalculating on every render
  const dataSource = useMemo(
    () => filterInvoices(allInvoices, filters),
    [allInvoices, filters],
  );

  // Memoize columns definition since it doesn't depend on component state
  const columns: ColumnsType<Invoice> = useMemo(
    () => [
      {
        key: 'createdAt',
        dataIndex: 'createdAt',
        title: 'Date',
        width: COLUMN_WIDTH,
        align: 'left',
        render: (createdAt: Date) => moment(createdAt).format('MMM D, YYYY'),
      },
      {
        key: 'invoiceNumber',
        dataIndex: 'invoiceNumber',
        title: 'Invoice Number',
        width: COLUMN_WIDTH,
        align: 'left',
        render: (invoiceNumber: string) => `Invoice #${invoiceNumber}`,
      },
      {
        key: 'amount',
        dataIndex: 'amount',
        title: 'Amount',
        width: COLUMN_WIDTH,
        align: 'left',
        filterDropdown: StatusFilterDropdown,
        render: (amount: number, record: Invoice) => (
          <AmountCell>
            <span>{amount ? formatCurrency(amount) : '$0.00'}</span>
            <StatusPill $status={record.status}>
              {record.status === InvoiceStatus.Paid ? 'Paid' : 'Unpaid'}
            </StatusPill>
          </AmountCell>
        ),
      },
    ],
    [],
  );

  // Fetch invoices when billingProfile becomes available
  useEffect(() => {
    if (
      !billingProfile?.BoardBillingNumber ||
      !billingProfile?.UserBillingNumber
    ) {
      return;
    }

    let isMounted = true;

    const loadInvoices = async () => {
      setLoading(true);

      try {
        const response = await getKeyholderInvoices({
          supraOCN: billingProfile.BoardBillingNumber,
          billingCustomerNumber: billingProfile.UserBillingNumber,
        });

        if (!isMounted) return;

        const mappedInvoices = response.map(mapInvoiceResponse);
        setAllInvoices(mappedInvoices);
      } catch (error) {
        if (isMounted) {
          setAllInvoices([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadInvoices();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [billingProfile?.BoardBillingNumber, billingProfile?.UserBillingNumber]);

  /**
   * Handles date range picker changes.
   * Converts selected dates to start/end of day for accurate filtering.
   */
  const handleDateChange = (
    _: [moment.Moment, moment.Moment] | null,
    dateStrings: [string, string],
  ) => {
    const createdAfter = dateStrings?.[0]
      ? moment(dateStrings[0]).startOf('day').toDate()
      : undefined;
    const createdBefore = dateStrings?.[1]
      ? moment(dateStrings[1]).endOf('day').toDate()
      : undefined;

    setFilters(prev => ({
      ...prev,
      createdAfter,
      createdBefore,
    }));
  };

  /**
   * Sends billing history email to the user.
   * Uses current date filters if set.
   */
  const handleSendEmail = async () => {
    if (
      !billingProfile?.BoardBillingNumber ||
      !billingProfile?.UserBillingNumber
    ) {
      message.error('Unable to send email. Missing billing information.');
      return;
    }

    try {
      // Format dates for API (ISO 8601 format)
      const startDate = filters.createdAfter
        ? `${moment(filters.createdAfter).format().slice(0, 19)}Z`
        : undefined;
      const endDate = filters.createdBefore
        ? `${moment(filters.createdBefore).format().slice(0, 19)}Z`
        : undefined;

      setSendingBillingHistoryEmail(true);

      await sendSupraBillingHistoryEmail({
        supraOCN: billingProfile.BoardBillingNumber,
        billingCustomerNumber: billingProfile.UserBillingNumber,
        startDate,
        endDate,
      });

      message.success('Email sent. Check your inbox.');
    } catch (e) {
      message.error(
        'Something went wrong. If this issue persists, please contact support.',
      );
    } finally {
      setSendingBillingHistoryEmail(false);
    }
  };

  /**
   * Handles table filter changes from the status filter dropdown.
   */
  const handleTableChange = (
    _pagination: TablePaginationConfig,
    filtersValues: Record<string, FilterValue | null>,
  ) => {
    setFilters(prev => ({
      ...prev,
      status:
        filtersValues.amount?.length === 1
          ? (filtersValues.amount[0] as InvoiceStatus)
          : undefined,
    }));
  };

  const isLoading = loadingBillingProfile || loading;

  return (
    <StyledCard
      title="Invoices"
      extra={
        <StyledExtra>
          <StyledEmailButton
            type="primary"
            loading={sendingBillingHistoryEmail}
            disabled={!hasBillingProfile || sendingBillingHistoryEmail}
            onClick={handleSendEmail}
          >
            Email Invoices
          </StyledEmailButton>
          <StyledRangePicker
            onChange={handleDateChange}
            value={[
              filters.createdAfter
                ? moment(filters.createdAfter).startOf('day')
                : undefined,
              filters.createdBefore
                ? moment(filters.createdBefore).endOf('day')
                : undefined,
            ]}
            allowClear
            disabled={!hasBillingProfile}
          />
        </StyledExtra>
      }
      dataCy="invoices-card"
    >
      <Table
        dataSource={dataSource}
        rowKey="id"
        columns={columns}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} invoices`,
        }}
        loading={isLoading}
        scroll={{ x: 'max-content' }}
        onChange={handleTableChange}
        locale={{
          emptyText: <StyledNoContent>No invoices found</StyledNoContent>,
        }}
      />
    </StyledCard>
  );
};

export default InvoicesCard;

// Re-export types for consumers
export { InvoiceStatus } from './types';
export type { Invoice, InvoiceFilters } from './types';

// ============================================================================
// Styled Components
// ============================================================================

const StyledCard = styled(Card)`
  /* Card title styling */
  .ant-card-head-title {
    font-size: 20px;
    font-weight: 500;
    color: #1a1b21;
  }

  /* Override ant horizontal rule */
  .ant-card-head {
    border-bottom: none;
  }

  /* Card body padding */
  .ant-card-body {
    padding: 0 24px 24px 24px;
  }

  /* Column header styling */
  .ant-table-thead > tr > th {
    font-size: 16px;
    font-weight: 500;
    padding: 12px 16px;
  }

  /* Fix Amount column filter - title left with padding, icon right */
  .ant-table-thead > tr > th.ant-table-column-has-filters {
    .ant-table-filter-column {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: 0 8px;
    }

    .ant-table-column-title {
      flex: none;
    }

    .ant-table-filter-trigger-container,
    .ant-table-filter-trigger {
      position: static;
      margin-left: auto;
    }
  }
`;

const StyledExtra = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 10px;
  padding: 12px 0px;
  align-items: center;
`;

const StyledEmailButton = styled(Button)`
  && {
    font-size: 16px;
    padding-top: 0;
    padding-bottom: 0;
    font-weight: 500;
  }
`;

const StyledRangePicker = styled(RangePicker)`
  && {
    width: ${DATE_PICKER_WIDTH}px;
    background-color: #ffffff;
    border: 1px solid #cdcdcd;
    border-radius: 4px;

    &,
    &.ant-picker,
    .ant-picker-input,
    .ant-picker-input > input,
    .ant-picker-range-separator,
    .ant-picker-suffix,
    .ant-picker-clear,
    .ant-picker-active-bar {
      background-color: #ffffff;
      background: #ffffff;
    }

    /* Override any internal backgrounds */
    * {
      background-color: transparent;
    }

    /* Remove blue highlight on hover and focus */
    &:hover,
    &:focus,
    &.ant-picker-focused,
    &.ant-picker:hover,
    &.ant-picker-focused.ant-picker {
      border-color: #cdcdcd;
      background-color: #ffffff;
      box-shadow: none;
      outline: none;
    }

    /* Remove active bar highlight */
    .ant-picker-active-bar {
      background: transparent;
      display: none;
    }

    /* Remove input focus styles */
    .ant-picker-input > input:focus,
    .ant-picker-input > input:hover {
      box-shadow: none;
      outline: none;
    }
  }
`;

const AmountCell = styled.div`
  display: inline-flex;
  align-items: center;

  > span:first-child {
    min-width: ${AMOUNT_MIN_WIDTH}px;
    text-align: left;
    margin-right: 8px;
  }
`;

const StatusPill = styled.span<{ $status: InvoiceStatus }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 0 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid
    ${({ $status }) => ($status === InvoiceStatus.Paid ? '#a8ceff' : '#ff4d4f')};
  background-color: ${({ $status }) =>
    $status === InvoiceStatus.Paid ? '#f0f7ff' : '#ffccc7'};
  color: ${({ $status }) =>
    $status === InvoiceStatus.Paid ? '#1b4298' : '#ffffff'};
`;

const StyledNoContent = styled(Text)`
  color: ${theme.getColor('textSecondary')};
  padding: 30px;
`;
